// ============================================================================
// categoriasAjuda — "quando usar" de cada categoria + catálogo da tela (07/10/2026)
// ============================================================================
// O plano de contas OFICIAL vem do servidor: /api/fin?op=categorias devolve as
// fin_categorias ATIVAS (código, nome, linha da DRE, bloco, grupo, ordem,
// a_validar, observação) e o de-para (fin_categoria_alias: valor gravado →
// código). Aqui ficam:
//   - AJUDA_CATEGORIAS: código → { quando, exemplos, cuidado } — o texto curto
//     que aparece embaixo do dropdown (exemplos tirados dos títulos reais);
//   - AJUDA_ENTRADAS: o mesmo para as categorias de ENTRADA;
//   - GRUPOS_DRE: como o dropdown agrupa (pela linha da DRE);
//   - montarCatalogo(): lista agrupada + de-para + rótulo de qualquer valor
//     gravado (inclusive os antigos: 'Aluguel', 'despesas_financeiras'…);
//   - catalogoReserva(): a lista antiga de expenseCategories.js, para quando a
//     leitura FALHA — a tela mostra o aviso (REGRA LINEAR: falha não vira
//     lista vazia nem some calada).
// Puro (sem React, sem I/O). Testes: tests/fin-cancelamento-telas.test.mjs.
// ============================================================================

import { SAIDA_CATEGORIES_GROUPED, saidaCategoryLabel } from './expenseCategories.js';
// 20/10/2026: a ajuda do vale (Adiantamento a funcionário e Vale de comissionado) segue FIN_VALES
import { FIN_VALES } from '../config/featureFlags.js';

// ─── Ajuda por código (fin_categorias) ─────────────────────────────────────
export const AJUDA_CATEGORIAS = {
  // 1 · Impostos sobre vendas
  impostos_vendas: {
    quando: 'Guia do imposto sobre o que a unidade VENDEU: DAS do Simples Nacional da empresa (ou ISS). Lance a guia inteira do mês.',
    exemplos: ['DAS Simples Nacional set/2026 — Moema'],
    cuidado: 'Com guia PAGA lançada, a DRE para de usar a estimativa de 6% da unidade e usa só o lançado — não lance pedaço. DAS do MEI de um barbeiro NÃO é imposto da empresa: lance em "Outras despesas operacionais", com "DAS MEI <nome>" no título (provisório, até o dono decidir).',
  },
  // 2 · Pessoal
  folha_salarios: {
    quando: 'Salário do mês NÃO se lança aqui à mão: use Folha de Pagamento → Marcar paga (a Folha cria o título sozinha). Aqui só o que a Folha não cobre: rescisão e Jovem Aprendiz/estágio.',
    exemplos: ['Rescisão Geovana', 'Sicoob Jovem Valor (jovem aprendiz)'],
    cuidado: 'Lançar o salário aqui E marcar paga na Folha conta o mesmo pagamento duas vezes.',
  },
  comissoes: {
    quando: 'Pagamento de comissão ao profissional. Na DRE o custo é a comissão APURADA no Avec; este lançamento serve para conferir apurado × pago.',
    exemplos: ['Comissão Victor 01/09 a 15/09', 'Pagamento comissão Fernando'],
    cuidado: FIN_VALES
      // 20/10/2026 (vales, FIN_VALES): o vale do comissionado tem categoria própria
      ? 'Escreva no título o nome e o período. Vale (adiantamento) não é comissão: de comissionado vai em "Vale de comissionado"; de quem está na folha, em "Adiantamento a funcionário". Prêmio/bônus NÃO é comissão (Comissões não soma na DRE): use Benefícios.'
      : 'Escreva no título o nome e o período. Adiantamento/vale que será descontado depois é "Adiantamento a funcionário". Prêmio/bônus NÃO é comissão (Comissões não soma na DRE): use Benefícios.',
  },
  beneficios: {
    quando: 'VT, VR e ajuda de custo pagos À PARTE da Folha.',
    exemplos: ['VT Kauanny', 'Ajuda de custo barbeiro líder', 'Bônus disputa Victor', 'Uber de serviço — Rafaela', 'Taxa iFood Benefício / Colab+'],
    cuidado: 'Se o VT/VR já está na Folha do mês, não lance de novo. Benefício de quem também recebe comissão ou Folha aparece na checagem (b) do mês: justifique no Checklist da DRE.',
  },
  encargos_folha: {
    quando: 'Guias de FGTS e INSS/DARF da folha. Contam no mês ANTERIOR ao vencimento (a guia de 20/10 é de setembro) e só depois de PAGAS.',
    exemplos: ['GUIA FGTS UNIDADE', 'GUIA DARF UNIDADE', 'CEF MATRIZ (FGTS)'],
    cuidado: 'A guia vem do contador, mas não é "Contabilidade": é encargo da folha.',
  },
  decimo_terceiro_ferias: {
    quando: '13º salário e férias — só no mês em que forem PAGOS (sem provisão mensal).',
    exemplos: ['13º 1ª parcela — Mariana', 'Férias Julia'],
  },
  provisao_13_ferias: {
    quando: 'Não usar (a DRE não provisiona 13º/férias — decisão do dono).',
    exemplos: [],
  },
  // 3 · Insumos
  produtos_uso: {
    quando: 'Produto usado no atendimento e insumos do salão: lâminas, escovas, produtos (Viking, Pro Beleza) para USAR, produto de limpeza.',
    exemplos: ['Viking', 'Compra de lâminas', 'Ecolimp produtos de limpeza'],
    cuidado: 'Produto comprado para VENDER ao cliente é "Produtos para revenda".',
  },
  lavanderia: {
    quando: 'Lavagem de toalhas e capas.',
    exemplos: ['Interlux Lavanderia', 'Lavagem de capas'],
    cuidado: 'Interlux (lavagem e locação de toalhas, capas e roupões) fica toda aqui, todo mês. Uniforme comprado é "Uniformes".',
  },
  copa_cafe: {
    quando: 'Café, água para clientes, máquina de café (mensalidade/locação), xícaras, lanche de reunião.',
    exemplos: ['Mensalidade máquina de café', 'Compra de água', 'Pizza reunião'],
    cuidado: 'Conta de água da Sabesp é "Água".',
  },
  // 4 · Revenda
  produtos_revenda: {
    quando: 'Mercadoria comprada para REVENDER ao cliente (pomada, shampoo da vitrine).',
    exemplos: ['Viking (produtos da vitrine)'],
  },
  // 4.5 · Ocupação
  aluguel: {
    quando: 'Aluguel do imóvel (ponto) da unidade. Vale o mês do VENCIMENTO. Lance o valor real do boleto.',
    exemplos: ['Aluguel Jardins out/2026'],
    cuidado: 'Um título por mês (não crie 60 parcelas). Reajuste: corrija o valor no mês em que mudar. Vaga de estacionamento alugada por mês é "Valet / manobrista" (mesma linha da DRE) — em Aluguel a checagem acusa 2 aluguéis no mês.',
  },
  condominio: { quando: 'Condomínio do imóvel.', exemplos: ['Condomínio Oscar Freire out/2026'] },
  iptu: { quando: 'IPTU — cada parcela no mês em que for paga.', exemplos: ['IPTU 2026 parcela 8/10 — Itaim'] },
  energia: { quando: 'Conta de luz.', exemplos: ['Enel — Vila Olímpia set/2026'] },
  agua: {
    quando: 'Conta de água (Sabesp).',
    exemplos: ['Sabesp — Moema set/2026'],
    cuidado: 'Água para beber/clientes é "Copa e café".',
  },
  seguro: { quando: 'Seguro do imóvel ou da unidade.', exemplos: ['Seguro empresarial Jardins'] },
  alarme: { quando: 'Alarme e monitoramento.', exemplos: ['Verisure'] },
  valet: {
    quando: 'Serviço de valet/manobrista e vaga de estacionamento alugada por mês.',
    exemplos: ['Valet Jardins', 'Locação vaga Jardins'],
    cuidado: 'A vaga mensal vem aqui (não em Aluguel): mesma linha da DRE, sem acusar "2 aluguéis no mês".',
  },
  limpeza: {
    quando: 'Diarista, faxina e limpeza terceirizada (ocupação — decisão do dono).',
    exemplos: ['Marina diarista', 'Limpeza de vidros Jardins'],
    cuidado: 'Produto de limpeza é "Produtos de uso e insumos".',
  },
  manutencao: {
    quando: 'Conserto e manutenção: ar-condicionado, cadeiras, telhado, vidro, elétrica.',
    exemplos: ['Conserto cadeiras', 'Manutenção ar-condicionado'],
    cuidado: 'Obra grande que melhora o imóvel é "Reformas"; equipamento novo é "Aquisição de equipamentos".',
  },
  // 5 · Administrativo
  contabilidade: {
    quando: 'Honorários do contador.',
    exemplos: ['Honorários contabilidade Moema'],
    cuidado: 'Guia que o contador manda (DARF, FGTS, DAS, prefeitura) NÃO é contabilidade: use a categoria do imposto. Mensalidade do contador do MEI de barbeiro também não: "Outras despesas operacionais" (provisório).',
  },
  juridico: { quando: 'Contratos, homologação, cartório e advogado.', exemplos: ['Contrato homologação Luiz', 'Custas cartório'] },
  sistemas_software: { quando: 'Avec e outros sistemas/aplicativos.', exemplos: ['Sistema Avec — Itaim'] },
  telefone_internet: { quando: 'Telefone e internet.', exemplos: ['Vivo — Oscar Freire'] },
  compras_gerais: {
    quando: 'Compras pequenas da unidade que não são produto de atendimento: utensílios, materiais, entregas.',
    exemplos: ['Compras unidade Itaim', 'Uber entrega'],
  },
  uniformes: { quando: 'Uniforme comprado para a equipe.', exemplos: ['Camisetas da equipe'], cuidado: 'Interlux (toalhas, roupões, lavagem ou locação) é "Lavanderia".' },
  despesas_administrativas: {
    quando: 'Rótulo antigo — não use. Escolha a categoria específica.',
    exemplos: [],
  },
  taxas_licencas: { quando: 'Taxas da prefeitura: TFE, alvará, licenças.', exemplos: ['TFE Unidade Moema', 'Prefeitura unidade Moema'] },
  // 6 · Marketing
  marketing: {
    quando: 'Agência, tráfego pago, videomaker, gráfica, impulsionamento.',
    exemplos: ['Dose de Growth assessoria digital', 'Videomaker', 'Gráfica'],
    cuidado: 'Se é para todas as unidades, em "Unidade que usa o custo" escolha Rede.',
  },
  // 7 · Taxas financeiras
  tarifas_bancarias: { quando: 'Tarifas do banco: pacote, PIX, TED, boleto.', exemplos: ['Tarifa pacote Santander set/2026'] },
  mdr_antecipacao: {
    quando: 'Taxa da maquininha (MDR) e antecipação — o total do mês, pelo relatório da maquininha.',
    exemplos: ['Taxas maquininha set/2026 — Vila'],
    cuidado: 'Com valor PAGO lançado, a DRE troca a estimativa de 3% da unidade pelo lançado — lance o mês inteiro, não parte.',
  },
  // 9 · Outras
  treinamento_educacao: {
    quando: 'Cursos e treinamento. JB Educação é custo da REDE.',
    exemplos: ['JB Educação (rede)', 'Curso de visagismo'],
    cuidado: 'JB Educação: em "Unidade que usa o custo" escolha Rede. Jovem Valor/Jovem Aprendiz é pessoal ("Salários e folha").',
  },
  reembolsos: {
    quando: 'Só para devolver dinheiro a CLIENTE (estorno).',
    exemplos: ['Estorno hidratação cliente'],
    cuidado: 'Reembolso a funcionário que comprou algo para a unidade vai na categoria do que foi comprado (bolo → Copa e café; conserto → Manutenção), com "reembolso <nome>" no título.',
  },
  outros_saida: {
    quando: 'Só quando nenhuma outra categoria serve — escreva no título o que é.',
    exemplos: ['Medicina admissional', 'DAS MEI Silvio (provisório)', 'Contador MEI Silvio (provisório)'],
    cuidado: 'Ajuda de custo é Benefícios; valet é Valet; Sabesp é Água.',
  },
  // 10 · A classificar
  a_classificar: {
    quando: 'Não escolha de propósito: é onde cai o que ninguém classificou. Trava o fechamento da DRE até alguém classificar.',
    exemplos: [],
  },
  // 20 · Investimentos (fora do resultado)
  investimentos: { quando: 'Investimento na unidade (ex.: projeto da Moema nova). Fica fora do resultado do mês.', exemplos: ['Moema nova — projeto'] },
  aquisicao_equipamentos: { quando: 'Equipamento durável novo: cadeira, ar-condicionado, máquina, computador.', exemplos: ['Cadeira de barbeiro nova'] },
  reformas: { quando: 'Obra/reforma que melhora o imóvel.', exemplos: ['Reforma Moema nova'] },
  // 21 · Sócios
  prolabore_pago: { quando: 'Pró-labore do sócio (só informação — fora do resultado).', exemplos: ['Pró-labore Marcelo set/2026'] },
  retirada_socio: { quando: 'Dinheiro que o sócio tirou da empresa.', exemplos: ['Retirada sócio'] },
  distribuicao_lucros: { quando: 'Distribuição de lucro aos sócios.', exemplos: [] },
  adiantamento_socio: { quando: 'Adiantamento ao sócio que será acertado depois.', exemplos: [] },
  // 22–26 · Dívida e grupo
  mutuo_grupo: { quando: 'Dinheiro emprestado entre empresas do grupo (CNPJs diferentes). Não é custo.', exemplos: ['Mútuo Jardins → Oscar'] },
  emprestimo_principal: {
    quando: 'Parcela de empréstimo: a parte que ABATE a dívida (principal).',
    exemplos: ['Empréstimo Santander parcela 3/24 — principal'],
    cuidado: 'Os juros da mesma parcela vão em outro título, em "Juros e encargos de dívida".',
  },
  juros_encargos: { quando: 'Juros, multa e IOF de empréstimo ou de atraso.', exemplos: ['Empréstimo Santander parcela 3/24 — juros'] },
  parcelamento_tributario: { quando: 'Parcela de imposto atrasado parcelado (o principal).', exemplos: ['Parcelamento Simples 5/60'] },
  irpj_csll: { quando: 'Não se aplica no Simples Nacional.', exemplos: [] },
  // Fora da DRE (movimentos internos)
  transferencia_saida: {
    quando: 'Dinheiro que só muda de conta da MESMA empresa (banco → caixa, conta → conta). Não é despesa.',
    exemplos: ['Transferência Santander → caixa'],
  },
  sangria: { quando: 'Retirada de dinheiro da gaveta do caixa (feita na tela Caixas).', exemplos: [] },
  suprimento: { quando: 'Troco colocado na gaveta do caixa (feito na tela Caixas).', exemplos: [] },
  adiantamento_funcionario: FIN_VALES ? {
    // 20/10/2026 (vales, FIN_VALES): sem o exemplo com nome; comissionado vai na categoria própria
    quando: 'Vale (adiantamento) de quem está na folha, por PIX da Stone: escolha o profissional e ele é descontado no próximo pagamento. Não é custo (é dinheiro a receber).',
    exemplos: ['Vale da recepcionista, descontado na folha de nov/2026'],
    cuidado: 'Só para quem está na folha; comissionado vai em "Vale de comissionado" (o Avec desconta). Não lance o mesmo vale no Avec.',
  } : {
    quando: 'Vale/adiantamento ao funcionário que será DESCONTADO depois. Não é custo (é dinheiro a receber).',
    exemplos: ['Vale Caio'],
  },
  emprestimo_funcionario: { quando: 'Empréstimo ao funcionário, com devolução combinada.', exemplos: ['Empréstimo Jessica'] },
  // 20/10/2026 (financeiro-013, vales): o vale de quem não está na folha
  vale_comissionado: {
    quando: 'Vale (adiantamento) por PIX da Stone para quem NÃO está na folha (barbeiro, manicure comissionados): escolha o profissional. O Avec desconta da comissão; aqui só fica o registro.',
    exemplos: ['Vale da manicure comissionada, out/2026'],
    cuidado: 'É custo de pessoal na unidade da pessoa (a comissão do Avec já vem sem o vale). Quem está na folha vai em "Adiantamento a funcionário".',
  },
};

// ─── Ajuda das ENTRADAS (MOVEMENT_CATEGORIES.entrada) ──────────────────────
export const AJUDA_ENTRADAS = {
  receita_mes: {
    quando: 'Em geral NÃO precisa lançar: desde 07/10/2026 a receita do Fluxo de Caixa vem do faturamento do Avec (e a da DRE também). Só vale num mês SEM faturamento do Avec: total recebido de vendas no mês (pelo extrato), sem aporte, empréstimo, estorno nem troco.',
    exemplos: ['Receita de um mês sem Avec — Moema (extrato)'],
    cuidado: 'Num mês com Avec este lançamento é IGNORADO no Fluxo (aparece riscado), para não contar a venda duas vezes. Aporte do sócio e empréstimo recebido NÃO entram aqui: use "Deposito".',
  },
  venda_comanda: { quando: 'Não use: as vendas vêm do Avec.', exemplos: [] },
  suprimento: { quando: 'Troco colocado no caixa — use a tela Caixas.', exemplos: [] },
  receita_extra: {
    quando: 'Entrada que não é venda do salão (ex.: parceria, sublocação). Escreva a origem no título.',
    exemplos: [],
    cuidado: 'No Fluxo de Caixa, num mês com faturamento do Avec, aparece em "Outras entradas (não é venda)": entra no saldo, mas não na receita nem na geração de caixa.',
  },
  deposito: {
    quando: 'Dinheiro que entrou e não é venda: aporte do sócio, empréstimo recebido, dinheiro recebido de outra unidade. Escreva a origem no título.',
    exemplos: ['Aporte sócio', 'Empréstimo Santander recebido'],
    cuidado: 'No Fluxo de Caixa aparece em "Outras entradas (não é venda)": entra no saldo, mas não na receita. Não some este valor também na "Receita do mês".',
  },
  estorno: {
    quando: 'Dinheiro devolvido para a empresa: estorno de cobrança, reembolso recebido de fornecedor.',
    exemplos: [],
    cuidado: 'No Fluxo de Caixa aparece em "Outras entradas (não é venda)".',
  },
  outros_entrada: {
    quando: 'Só se nada acima servir — escreva no título o que é.',
    exemplos: [],
    cuidado: 'No Fluxo de Caixa conta como "Outras entradas (não é venda)", não como receita.',
  },
};

export const ajudaDe = (codigo) => AJUDA_CATEGORIAS[codigo] || null;
export const ajudaEntrada = (codigo) => AJUDA_ENTRADAS[codigo] || null;

// ─── Agrupamento pela linha da DRE ─────────────────────────────────────────
export const GRUPOS_DRE = [
  { chave: '1', titulo: 'Impostos sobre vendas (DRE linha 1)' },
  { chave: '2', titulo: 'Pessoal (DRE linha 2)' },
  { chave: '3', titulo: 'Insumos (DRE linha 3)' },
  { chave: '4', titulo: 'Produtos para revenda (DRE linha 4)' },
  { chave: '4.5', titulo: 'Ocupação (DRE linha 4.5)' },
  { chave: '5', titulo: 'Administrativo (DRE linha 5)' },
  { chave: '6', titulo: 'Marketing (DRE linha 6)' },
  { chave: '7', titulo: 'Taxas financeiras (DRE linha 7)' },
  { chave: '9', titulo: 'Outras despesas (DRE linha 9)' },
  // 20/10/2026 (revisão dos vales): sem travessão (aparecem no Centro de Custo do vale e nos grupos da escolha)
  { chave: '10', titulo: 'A classificar (DRE linha 10, trava o fechamento)' },
  { chave: '20', titulo: 'Investimentos: fora do resultado' },
  { chave: '21', titulo: 'Sócios: fora do resultado' },
  { chave: 'divida', titulo: 'Dívidas e grupo: abaixo do resultado' },
  { chave: 'fora', titulo: 'Movimentos internos: não é despesa' },
];

export function grupoDaCategoria(c) {
  const linha = c?.linha_dre == null || c?.linha_dre === '' ? null : Number(c.linha_dre);
  if (c?.bloco === 'fora' || linha == null || Number.isNaN(linha)) return 'fora';
  if (linha >= 22) return 'divida';
  return String(linha);
}

const normValor = (s) => String(s ?? '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Catálogo da tela a partir da resposta do op=categorias.
//   { fonte, categorias, grupos:[{chave,titulo,itens}], porCodigo, codigoDe(v),
//     rotulo(v), ehLegado(v) }
export function montarCatalogo({ categorias = [], alias = [] } = {}, { fonte = 'servidor' } = {}) {
  const lista = (categorias || [])
    .filter(c => c && typeof c.codigo === 'string' && c.codigo && c.ativo !== false)
    .map(c => ({
      codigo: c.codigo, nome: c.nome || saidaCategoryLabel(c.codigo),
      linha_dre: c.linha_dre == null || c.linha_dre === '' ? null : Number(c.linha_dre),
      bloco: c.bloco || null, grupo_ui: c.grupo_ui || null, ordem: Number(c.ordem) || 0,
      a_validar: !!c.a_validar, observacao: c.observacao || null, ajuda: ajudaDe(c.codigo),
    }));
  const porCodigo = new Map(lista.map(c => [c.codigo, c]));

  // de-para como o banco (fin_categoria_codigo): exato; depois sem caixa/acento
  // (empate → menor código); vazio/desconhecido → 'a_classificar'.
  const exato = new Map();
  const norm = new Map();
  for (const a of (alias || [])) {
    if (!a || !a.valor_atual || !a.codigo) continue;
    exato.set(a.valor_atual, a.codigo);
    const k = normValor(a.valor_atual);
    if (k && (!norm.has(k) || a.codigo < norm.get(k))) norm.set(k, a.codigo);
  }
  const servidor = fonte === 'servidor';
  const codigoDe = (valor) => {
    const v = valor == null ? '' : String(valor);
    if (exato.has(v)) return exato.get(v);
    if (porCodigo.has(v)) return v;
    const k = normValor(v);
    if (k && norm.has(k)) return norm.get(k);
    // reserva: sem de-para, o valor é o próprio código
    return servidor ? 'a_classificar' : (v || 'a_classificar');
  };
  const rotulo = (valor) => {
    if (valor == null || valor === '') return 'Sem categoria';
    const cod = codigoDe(valor);
    return porCodigo.get(cod)?.nome || saidaCategoryLabel(cod);
  };
  // valor gravado que não é um código da lista (rótulo antigo ou código legado)
  const ehLegado = (valor) => !!valor && !porCodigo.has(String(valor));

  const ordemGrupo = new Map(GRUPOS_DRE.map((g, i) => [g.chave, i]));
  const grupos = new Map();
  for (const c of lista) {
    const chave = grupoDaCategoria(c);
    if (!grupos.has(chave)) {
      grupos.set(chave, {
        chave, titulo: GRUPOS_DRE.find(g => g.chave === chave)?.titulo || `DRE linha ${chave}`, itens: [],
      });
    }
    grupos.get(chave).itens.push(c);
  }
  const gruposOrd = [...grupos.values()]
    .sort((a, b) => (ordemGrupo.get(a.chave) ?? 99) - (ordemGrupo.get(b.chave) ?? 99) || a.chave.localeCompare(b.chave))
    .map(g => ({ ...g, itens: g.itens.slice().sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome, 'pt-BR')) }));

  return { fonte, categorias: lista, grupos: gruposOrd, porCodigo, codigoDe, rotulo, ehLegado };
}

// Resposta do /api/fin?op=categorias → lança se vier vazia (categorias OU
// de-para): 200 com lista vazia viraria um dropdown só com "Selecione…" e todo
// valor antigo ('Aluguel', 'Limpeza'…) rotulado como "A classificar", calado.
// Quem chama cai na lista de RESERVA, com o aviso (REGRA LINEAR: erro ≠ vazio).
export function validarRespostaCategorias(r) {
  const cats = Array.isArray(r?.categorias) ? r.categorias.filter(c => c && c.codigo) : [];
  const alias = Array.isArray(r?.alias) ? r.alias.filter(a => a && a.valor_atual && a.codigo) : [];
  if (!cats.length) throw new Error('O plano de contas veio vazio (vazio não é "sem categorias").');
  if (!alias.length) throw new Error('O de-para das categorias antigas veio vazio (vazio não é "sem de-para").');
  return { categorias: r.categorias, alias: r.alias };
}

// Lista de RESERVA: a lista antiga (expenseCategories.js), sem de-para. Só
// quando a leitura do plano de contas falhou — a tela avisa.
export function catalogoReserva() {
  const cats = [];
  let ordem = 0;
  for (const g of SAIDA_CATEGORIES_GROUPED) {
    for (const i of g.items) cats.push({ codigo: i.value, nome: i.label, grupo_ui: g.group, ordem: ordem++ });
  }
  const cat = montarCatalogo({ categorias: cats, alias: [] }, { fonte: 'reserva' });
  // agrupa pelos grupos antigos (não há linha da DRE na lista antiga)
  const grupos = SAIDA_CATEGORIES_GROUPED.map(g => ({
    chave: g.group, titulo: `${g.group} (lista antiga)`,
    itens: g.items.map(i => cat.porCodigo.get(i.value)).filter(Boolean),
  }));
  return { ...cat, grupos };
}
